import { Box, Stack } from '@mui/material';
import { useWatch } from 'react-hook-form';

import { FormAwareDatePickerInput } from '@/components/form/formAwareInputs/FormAwareDatePickerInput';
import { FormAwareSelectInput } from '@/components/form/formAwareInputs/FormAwareSelectInput';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { Callout } from '@/components/notifications/Callout/Callout';
import { useFormContext } from '@/components/react-hook-form';
import { isValidTenantSubdomain } from '@/modules/tenant/tenant.utils';
import {
  TenantKind,
  TenantProductTier,
  TenantUsageBucket,
} from '@/types/schema';
import { getEnvironment } from '@/utils/environmentUtils';

import { usageBucketOptions } from '../AdminTenantsPage.constants';
import { CreateTenantFormShape } from './CreateTenantForm.types';

interface CreateTenantFormProps {
  nameLabel?: string;
}

export function CreateTenantForm({
  nameLabel = 'Organization display name',
}: CreateTenantFormProps = {}) {
  const environment = getEnvironment();
  const {
    control,
    formState: { isSubmitting },
  } = useFormContext<CreateTenantFormShape>();

  const tier = useWatch<CreateTenantFormShape>({
    control,
    name: 'tier',
  });

  const usageBucket = useWatch<CreateTenantFormShape>({
    control,
    name: 'usageBucket',
  });

  return (
    <Stack spacing={2}>
      <Callout severity="info-high">
        You are creating a new tenant in the {environment.toUpperCase()}{' '}
        environment.
      </Callout>
      <FormAwareTextInput<CreateTenantFormShape>
        label={nameLabel}
        helpText="To be used in places like proposals and invitation emails"
        fieldName={'name'}
        required
        disabled={isSubmitting}
        control={control}
      />
      <FormAwareTextInput<CreateTenantFormShape>
        validation={{
          pattern: (value) => {
            if (!value) return undefined;
            if (typeof value !== 'string') {
              throw new Error(
                'Unexpected type for subdomain validation: ' + typeof value
              );
            }

            if (isValidTenantSubdomain(value)) return undefined;
            return 'Subdomain must be lowercase and contain only letters and hyphens.';
          },
        }}
        label="Tenant subdomain"
        helpText="If you want to create the tenant 'customer.withluminary.com', enter 'customer' here (without the quotes)."
        fieldName={'subdomain'}
        required
        disabled={isSubmitting}
        control={control}
      />
      <Box display="flex" gap={2}>
        <Box flex={1}>
          <FormAwareSelectInput<CreateTenantFormShape>
            label="Usage bucket"
            fieldName="usageBucket"
            required
            disabled={isSubmitting}
            control={control}
            options={usageBucketOptions}
          />
        </Box>
        <Box flex={1}>
          <FormAwareSelectInput<CreateTenantFormShape>
            label="Production level"
            helpText="Production tenants are for real customers, non-production tenants are for testing, development, and demos."
            fieldName={'kind'}
            required
            disabled={isSubmitting}
            control={control}
            options={[
              {
                display: 'Production',
                value: TenantKind.Production,
              },
              {
                display: 'Non-Production',
                value: TenantKind.NonProduction,
              },
            ]}
          />
        </Box>
      </Box>
      {usageBucket === TenantUsageBucket.UnpaidTrial && (
        <FormAwareDatePickerInput<CreateTenantFormShape>
          label="Trial end date"
          fieldName="trialEndDate"
          helpText="The date when the tenant's trial period ends"
          disabled={isSubmitting}
          control={control}
        />
      )}
      <FormAwareSelectInput<CreateTenantFormShape>
        label="Product tier"
        fieldName="tier"
        required
        disabled={isSubmitting}
        control={control}
        options={[
          {
            display: 'Go',
            value: TenantProductTier.Go,
          },
          {
            display: 'Pro',
            value: TenantProductTier.Pro,
          },
          {
            display: 'Enterprise',
            value: TenantProductTier.Enterprise,
          },
        ]}
      />
      {tier === TenantProductTier.Go && (
        <FormAwareSelectInput<CreateTenantFormShape>
          label="Asset provider integration add-on"
          fieldName="addonAssetProviderIntegrationEnabled"
          disabled={isSubmitting}
          helpText="Only applicable to Go tier; included by default for other tiers"
          control={control}
          options={[
            {
              display: 'Enabled',
              value: true,
            },
            {
              display: 'Disabled',
              value: false,
            },
          ]}
        />
      )}
    </Stack>
  );
}
